import { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../../utils/api';
import { Card, Spinner } from '../shared/UI';

const MUTED_BLUE = '#3b82f6';
const MUTED_BLUE_FILL = 'rgba(59,130,246,0.08)';
const TICK_STYLE = { fontSize: 11, fill: '#64748b', fontFamily: 'DM Sans, sans-serif' };

function fmtDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

function calcYMax(data) {
  const max = Math.max(...data.map(d => d.count), 0);
  if (max <= 70) return 80;
  return Math.ceil(max / 20) * 20 + 20;
}

function calcYTicks(max) {
  const ticks = [];
  const step = 20;
  for (let v = 0; v <= max; v += step) ticks.push(v);
  return ticks;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--gray-100)', borderRadius: 8,
      padding: '8px 14px', boxShadow: 'var(--shadow)', fontSize: 13,
    }}>
      <div style={{ color: 'var(--gray-500)', marginBottom: 2, fontSize: 12 }}>{fmtDate(label)}</div>
      <div style={{ fontWeight: 700, color: MUTED_BLUE }}>{payload[0].value} tickets</div>
    </div>
  );
}

export function DailyTrendChart({ trendData: externalData, trendLoading: externalLoading }) {
  const hasExternal = externalData !== undefined;
  const [internalData, setInternalData] = useState(hasExternal ? externalData : null);

  useEffect(() => {
    if (hasExternal) return;
    api.get('/api/tickets/daily-trend?days=30')
      .then(setInternalData)
      .catch(() => setInternalData([]));
  }, [hasExternal]);

  const data = hasExternal ? externalData : internalData;
  if (data === null || externalLoading) return <Spinner />;

  const active = data.filter(d => d.count > 0);
  const yMax = calcYMax(active.length ? active : data);
  const yTicks = calcYTicks(yMax);

  return (
    <Card>
      <div style={{ padding: '20px 24px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, color: 'var(--navy)' }}>Daily QMS Ticket Trend</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: 13, marginTop: 4 }}>Last {data.length} days · {active.length} active days</p>
      </div>
      <div style={{ padding: '0 16px 20px' }}>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={active} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={MUTED_BLUE} stopOpacity={0.2} />
                <stop offset="100%" stopColor={MUTED_BLUE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={fmtDate}
              tick={TICK_STYLE}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              domain={[0, yMax]}
              ticks={yTicks}
              tick={TICK_STYLE}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="count"
              stroke={MUTED_BLUE}
              strokeWidth={2.5}
              fill="url(#trendFill)"
              dot={false}
              activeDot={{ r: 5, fill: MUTED_BLUE, stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
